import { useCallback, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AgentSpec, AvailableModel, DefaultAgents, EffectiveLoadout, InvocationShapeInfo, Prompt,
} from '../../../types/backend';
import { useAgentSpecService } from '../services/agentSpecService';
import type { AgentSpecUpdatePayload } from '../services/agentSpecService';
import { usePromptService } from '../services/promptService';
import { isAgentShellPrompt } from '../utils/promptBuckets';
import { toolWarningsOf } from '../utils/toolWarnings';

export const agentSpecKeys = {
  list: () => ['prompt-studio', 'agent-specs'] as const,
  globalPrompts: () => ['prompt-studio', 'global-prompts'] as const,
  models: () => ['prompt-studio', 'available-models'] as const,
  loadout: (agentId?: string) => ['prompt-studio', 'effective-loadout', agentId] as const,
  shapes: () => ['prompt-studio', 'invocation-shapes'] as const,
  defaults: () => ['agentspec', 'default-agents'] as const,
};

/** A loadout as the editor holds it: every key present, because a write must carry all three. */
export interface ToolLoadoutDraft {
  tools: string[];
  blocks: string[];
  avp_visibility_filter: boolean;
}

export interface AgentSpecFormData {
  display_name: string;
  description: string;
  model_id: string;
  max_tokens: string;
  thinking_effort: string;
  context_window_size: string;
  system_prompt_ref: string;
}

const EMPTY_FORM: AgentSpecFormData = {
  display_name: '',
  description: '',
  model_id: '',
  max_tokens: '',
  thinking_effort: '',
  context_window_size: '',
  system_prompt_ref: '',
};

const specToForm = (spec: AgentSpec): AgentSpecFormData => ({
  display_name: spec.display_name ?? '',
  description: spec.description ?? '',
  model_id: spec.model_id ?? '',
  max_tokens: spec.max_tokens != null ? String(spec.max_tokens) : '',
  thinking_effort: spec.thinking_effort ?? '',
  context_window_size: spec.context_window_size != null ? String(spec.context_window_size) : '',
  system_prompt_ref: spec.system_prompt_ref ?? '',
});

const positiveIntError = (raw: string, { required }: { required: boolean }): string | null => {
  const trimmed = raw.trim();
  if (!trimmed) return required ? 'Required' : null;
  if (!/^\d+$/.test(trimmed) || Number(trimmed) <= 0) return 'Must be a positive whole number';
  return null;
};

// model_id + max_tokens are required: the runtime needs both to build a request and
// ConfigService fails loud without them, so a save that blanked either would break
// the agent. thinking_effort and context_window_size stay optional (the aws_specialist
// legitimately has neither) and can be cleared.
export const validate = (form: AgentSpecFormData): Record<keyof AgentSpecFormData, string | null> => ({
  display_name: form.display_name.trim() ? null : 'Display name is required',
  description: form.description.trim() ? null : 'Description is required',
  model_id: form.model_id.trim() ? null : 'Model ID is required',
  max_tokens: positiveIntError(form.max_tokens, { required: true }),
  thinking_effort: null,
  context_window_size: positiveIntError(form.context_window_size, { required: false }),
  system_prompt_ref: form.system_prompt_ref.trim() ? null : 'System prompt is required',
});

// A cleared optional field is sent as an explicit null (not omitted) so the backend's
// exclude_unset upsert clears the stored value; the name, description, model_id,
// max_tokens and system_prompt_ref are required and always sent (the runtime fails loud
// without a shell to resolve).
export const formToRequest = (agentId: AgentSpec['agent_id'], form: AgentSpecFormData): AgentSpecUpdatePayload => ({
  agent_id: agentId,
  display_name: form.display_name.trim(),
  description: form.description.trim(),
  model_id: form.model_id.trim(),
  max_tokens: Number(form.max_tokens),
  thinking_effort: form.thinking_effort ? form.thinking_effort : null,
  context_window_size: form.context_window_size.trim() ? Number(form.context_window_size) : null,
  system_prompt_ref: form.system_prompt_ref.trim(),
});

/**
 * Form state for the global agent specs. Creating and retiring one live in their own dialogs;
 * this edits the selected row. The reads are cached: the specs list and the global-prompt
 * options (the latter degrades to empty on failure so it never blocks editing). All
 * form/selection state stays local; a save patches the cached spec in place.
 *
 * `authoring` is the caller's agent_authoring switch as the server resolved it. Only with it on
 * are the shapes read, which the server refuses otherwise.
 */
export function useAgentSpecForm({ isOpen, active, authoring }: { isOpen: boolean; active: boolean; authoring: boolean }) {
  const service = useAgentSpecService();
  const promptService = usePromptService();
  const queryClient = useQueryClient();

  const enabled = isOpen && active;

  const [selectedId, setSelectedId] = useState<AgentSpec['agent_id'] | null>(null);
  const [formData, setFormData] = useState<AgentSpecFormData>(EMPTY_FORM);
  const [localError, setLocalError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [hasAttemptedSave, setHasAttemptedSave] = useState(false);

  const specsQuery = useQuery({
    queryKey: agentSpecKeys.list(),
    queryFn: () => service.listAgentSpecs(),
    enabled,
  });

  // Global prompts feed the system-prompt selector. Read independently so a
  // prompt-list failure degrades to empty options rather than blocking the editor.
  const promptsQuery = useQuery<Prompt[]>({
    queryKey: agentSpecKeys.globalPrompts(),
    queryFn: () => promptService.listGlobalPrompts(),
    enabled,
  });

  // What this environment can run. A failure degrades to the stored value alone rather than
  // blocking the editor, the same posture as the prompt list above.
  const modelsQuery = useQuery<AvailableModel[]>({
    queryKey: agentSpecKeys.models(),
    queryFn: () => service.listAvailableModels(),
    enabled,
  });

  // Each shape's allow-list bounds the tools an agent of it may be given (PS-UC-15 step 6, 6c).
  const shapesQuery = useQuery<InvocationShapeInfo[]>({
    queryKey: agentSpecKeys.shapes(),
    queryFn: () => service.listInvocationShapes(),
    enabled: enabled && authoring,
  });

  // The investigation agent and the automation agent (PO 2026-10-28). Only authoring moves them.
  const defaultsQuery = useQuery<DefaultAgents>({
    queryKey: agentSpecKeys.defaults(),
    queryFn: () => service.getDefaultAgents(),
    enabled: enabled && authoring,
  });

  const specs = useMemo(() => specsQuery.data ?? [], [specsQuery.data]);

  const selectedSpec = useMemo(
    () => specs.find((s) => s.agent_id === selectedId) ?? null,
    [specs, selectedId],
  );

  // The toolbox the agent runs now. Read rather than assumed: a saved loadout replaces the
  // stored one whole, so an editor starting from an empty list would wipe the agent's tools
  // the first time anyone saved.
  const loadoutQuery = useQuery<EffectiveLoadout>({
    queryKey: agentSpecKeys.loadout(selectedId ?? undefined),
    queryFn: () => service.getEffectiveLoadout(selectedId as AgentSpec['agent_id']),
    enabled: enabled && selectedId !== null,
  });

  // The draft carries the agent it was made for. Keyed, not cleared on selection, because a
  // draft that outlived its agent would be saved onto the next one: one agent's toolbox
  // written over another's, from a picker that looked right.
  const [draft, setDraft] = useState<{ agentId: string; value: ToolLoadoutDraft } | null>(null);
  const agentDraft = draft && draft.agentId === selectedId ? draft : null;

  const effectiveLoadout = loadoutQuery.data ?? null;
  const storedLoadout: ToolLoadoutDraft | null = useMemo(() => (effectiveLoadout
    ? {
        tools: effectiveLoadout.tools ?? [],
        blocks: effectiveLoadout.blocks ?? [],
        avp_visibility_filter: effectiveLoadout.avp_visibility_filter ?? false,
      }
    : null), [effectiveLoadout]);

  const loadout: ToolLoadoutDraft | null = agentDraft?.value ?? storedLoadout;

  // The callee draft is keyed to its agent for the same reason the loadout draft is.
  const [calleeDraft, setCalleeDraft] = useState<{ agentId: string; value: string[] } | null>(null);
  const agentCalleeDraft = calleeDraft && calleeDraft.agentId === selectedId ? calleeDraft : null;
  const callees = agentCalleeDraft?.value ?? selectedSpec?.callees ?? [];
  // Only an active specialist may be consulted (PS-UC-15 6b).
  const calleeOptions = useMemo(
    () => specs.filter((s) => s.shape === 'sub_agent' && s.status === 'active'),
    [specs],
  );

  // With authoring on, the tools the server offers the shape are what may be chosen, each with
  // the wall's warning; until they are read nothing is, rather than the whole catalog. With it
  // off the server serves no shapes, and the loadout's own limit stands.
  const shapeInfo = authoring && selectedSpec
    ? (shapesQuery.data ?? []).find((s) => s.shape === selectedSpec.shape) ?? null
    : null;
  const loadoutSelectable = useMemo(() => (authoring
    ? shapeInfo?.tools.map((t) => t.action_id) ?? null
    : effectiveLoadout?.selectable ?? null), [authoring, shapeInfo, effectiveLoadout]);
  const loadoutWarnings = useMemo(() => toolWarningsOf(shapeInfo), [shapeInfo]);
  const loadoutLoading = loadoutQuery.isLoading || (authoring && shapeInfo === null);

  // A retired row is history: the backend refuses any update to it (PS-UC-18 4d).
  const readOnly = selectedSpec?.status === 'retired';

  // Shell options are exactly the prompts the Agents bucket owns: system_prompt rows
  // other than the shared account-model primer.
  const shellPrompts = useMemo(
    () => (promptsQuery.data ?? []).filter(isAgentShellPrompt),
    [promptsQuery.data],
  );

  // Keep a still-valid selection, else default to the first spec once loaded. Gated on
  // `enabled` so it re-selects on reopen (after the on-close reset clears the id) but
  // never re-selects while the modal is closed.
  useEffect(() => {
    if (!enabled || specs.length === 0) return;
    setSelectedId((prev) => (prev && specs.some((s) => s.agent_id === prev) ? prev : specs[0].agent_id));
  }, [enabled, specs]);

  // Reset the form to the selected spec whenever the selection resolves.
  useEffect(() => {
    if (selectedSpec) {
      setFormData(specToForm(selectedSpec));
      setHasAttemptedSave(false);
    }
  }, [selectedSpec]);

  // The hook stays mounted while the modal is closed (isOpen is a prop), so clear the
  // form/selection/error on close: reopening re-selects a spec and rebuilds the form
  // from the server, discarding any abandoned edit or stale banner.
  useEffect(() => {
    if (!isOpen) {
      setSelectedId(null);
      setFormData(EMPTY_FORM);
      setLocalError(null);
      setSuccessMessage(null);
      setHasAttemptedSave(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  // Explicit retry after a failed load.
  const retry = useCallback(() => {
    setLocalError(null);
    specsQuery.refetch();
  }, [specsQuery]);

  const changeLoadout = useCallback((next: ToolLoadoutDraft) => {
    if (selectedId) setDraft({ agentId: selectedId, value: next });
  }, [selectedId]);

  const changeCallees = useCallback((next: string[]) => {
    if (selectedId) setCalleeDraft({ agentId: selectedId, value: next });
  }, [selectedId]);

  const handleSelect = useCallback((spec: AgentSpec) => {
    setSelectedId(spec.agent_id);
    setLocalError(null);
  }, []);

  const handleChange = useCallback(<K extends keyof AgentSpecFormData>(field: K, value: AgentSpecFormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const rawErrors = useMemo(() => validate(formData), [formData]);
  const fieldErrors = useMemo(() => {
    if (!hasAttemptedSave) {
      return Object.fromEntries(Object.keys(rawErrors).map((k) => [k, null])) as Record<keyof AgentSpecFormData, string | null>;
    }
    return rawErrors;
  }, [hasAttemptedSave, rawErrors]);

  const loadoutChanged = useMemo(() => {
    if (agentDraft === null) return false;
    if (!storedLoadout) return true;
    const sameSet = (a: string[], b: string[]) =>
      a.length === b.length && a.every((v) => b.includes(v));
    return !sameSet(agentDraft.value.tools, storedLoadout.tools)
      || !sameSet(agentDraft.value.blocks, storedLoadout.blocks)
      || agentDraft.value.avp_visibility_filter !== storedLoadout.avp_visibility_filter;
  }, [agentDraft, storedLoadout]);

  const calleesChanged = agentCalleeDraft !== null && selectedSpec !== null && !(
    agentCalleeDraft.value.length === selectedSpec.callees.length
    && agentCalleeDraft.value.every((id) => selectedSpec.callees.includes(id))
  );

  const hasChanges = useMemo(() => {
    if (!selectedSpec) return false;
    if (loadoutChanged || calleesChanged) return true;
    const original = specToForm(selectedSpec);
    return (Object.keys(original) as (keyof AgentSpecFormData)[]).some((k) => original[k] !== formData[k]);
  }, [selectedSpec, formData, loadoutChanged, calleesChanged]);

  const saveMutation = useMutation({
    mutationFn: (spec: AgentSpec) => service.updateAgentSpec({
      ...formToRequest(spec.agent_id, formData),
      // Absent leaves the stored loadout alone; a complete object sets it. There is no
      // clearing it: no default in code stands behind the row (ADR-0064).
      ...(agentDraft === null ? {} : { tool_loadout: agentDraft.value }),
      // Sent only when changed: the server refuses a callee change while agent_authoring is off.
      ...(calleesChanged && agentCalleeDraft ? { callees: agentCalleeDraft.value } : {}),
    }),
    onSuccess: (updated) => {
      queryClient.setQueryData<AgentSpec[]>(agentSpecKeys.list(), (prev) =>
        prev?.map((s) => (s.agent_id === updated.agent_id ? updated : s)),
      );
      setHasAttemptedSave(false);
      setDraft(null);
      setCalleeDraft(null);
      queryClient.invalidateQueries({ queryKey: agentSpecKeys.loadout(updated.agent_id) });
      setSuccessMessage('Agent spec updated');
    },
    onError: (err) => setLocalError(err instanceof Error ? err.message : String(err)),
  });

  // Answers whether the row was written. A pane that saves this row and a prompt row behind
  // one button has to stop on a refusal here, or it half-applies the change and shows the
  // error and the success together.
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!selectedSpec || readOnly) return false;
    setHasAttemptedSave(true);
    if (Object.values(rawErrors).some((e) => e !== null)) {
      setLocalError('This agent cannot be saved yet: ' + Object.entries(rawErrors)
        .filter(([, e]) => e !== null)
        .map(([field, e]) => `${field} ${String(e).toLowerCase()}`)
        .join(', '));
      return false;
    }
    setLocalError(null);
    setSuccessMessage(null);
    try {
      await saveMutation.mutateAsync(selectedSpec);
      return true;
    } catch {
      return false;  // surfaced via onError
    }
  }, [selectedSpec, readOnly, rawErrors, saveMutation]);

  const defaultMutation = useMutation({
    mutationFn: (spec: AgentSpec) => {
      if (spec.shape === 'sub_agent') throw new Error(`Agent '${spec.agent_id}' is a sub_agent; a sub_agent has no default`);
      return service.setDefaultAgent({ shape: spec.shape, agent_id: spec.agent_id });
    },
    onSuccess: (defaults) => queryClient.setQueryData(agentSpecKeys.defaults(), defaults),
    onError: (err) => setLocalError(err instanceof Error ? err.message : String(err)),
  });

  const makeDefault = useCallback(async () => {
    if (!selectedSpec) return;
    setLocalError(null);
    try {
      await defaultMutation.mutateAsync(selectedSpec);
    } catch {
      // surfaced via onError
    }
  }, [selectedSpec, defaultMutation]);

  const loadError = specsQuery.isError
    ? `Failed to load agent specs: ${specsQuery.error instanceof Error ? specsQuery.error.message : String(specsQuery.error)}`
    : null;
  // A loadout that cannot be read is said out loud: without this the editor sits on
  // "reading what this agent runs..." for as long as the modal is open.
  const loadoutError = loadoutQuery.isError
    ? `Could not read what tools this agent runs: ${loadoutQuery.error instanceof Error ? loadoutQuery.error.message : String(loadoutQuery.error)}`
    : null;
  const shapesError = shapesQuery.isError
    ? `Could not read the tools this agent's shape allows: ${shapesQuery.error instanceof Error ? shapesQuery.error.message : String(shapesQuery.error)}`
    : null;
  const defaultsError = defaultsQuery.isError
    ? `Could not read the default agents: ${defaultsQuery.error instanceof Error ? defaultsQuery.error.message : String(defaultsQuery.error)}`
    : null;
  const error = localError ?? loadError ?? loadoutError ?? shapesError ?? defaultsError;

  return {
    specs,
    shellPrompts,
    promptsLoading: promptsQuery.isLoading,
    // isFetching (not isLoading) so a manual retry() after a failed load also reflects
    // the in-flight request.
    loading: specsQuery.isFetching,
    // Spinner-until-first-data: covers the initial load and a retry (both fetch with no
    // data yet), but not a background refetch once specs are present.
    initialLoading: specsQuery.isFetching && specsQuery.data === undefined,
    selectedSpec,
    formData,
    models: modelsQuery.data ?? [],
    modelsLoading: modelsQuery.isLoading,
    loadout,
    loadoutSelectable,
    loadoutWarnings,
    loadoutLoading,
    shapes: shapesQuery.data ?? [],
    defaults: defaultsQuery.data ?? null,
    makeDefault,
    settingDefault: defaultMutation.isPending,
    readOnly,
    changeLoadout,
    callees,
    calleeOptions,
    changeCallees,
    saving: saveMutation.isPending,
    error,
    successMessage,
    fieldErrors,
    hasChanges,
    retry,
    handleSelect,
    handleChange,
    handleSave,
    setError: setLocalError,
  };
}
